import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import VisitasCard from './VisitasCard';
import { DollarSign, Calendar, BarChart3, TrendingUp, Scissors, Stethoscope, Target, Gem, ArrowLeft, BarChart2 } from 'lucide-react';

// =====================================================
// COMPONENTE GENÉRICO REPORTS - PARA SALUD Y BELLEZA
// =====================================================

interface ReportData {
  totalRevenue: number;
  totalAppointments: number;
  avgTicket: number;
  clientRetention: number;
  popularServices: Array<{
    name: string;
    count: number;
    revenue: number;
  }>;
  monthlyTrend: Array<{
    month: string;
    appointments: number;
    revenue: number;
  }>;
  topClients: Array<{
    name: string;
    totalSpent: number;
    appointments: number;
  }>;
}

interface ThemeConfig {
  gradient: string;
  bgGradient: string;
  primary: string;
  reportTitle: string;
  icons: {
    revenue: React.ReactNode;
    appointments: React.ReactNode;
    ticket: React.ReactNode;
    retention: React.ReactNode;
  };
}

const Reports: React.FC = () => {
  const navigate = useNavigate();
  const [reportData, setReportData] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState('month');
  const [error, setError] = useState<string | null>(null);
  const [user, setUser] = useState<any>(null);

  // Configurar tema según tipo de profesional
  const getThemeConfig = (professionalType: 'salud' | 'belleza'): ThemeConfig => {
    if (professionalType === 'belleza') {
      return {
        gradient: 'from-pink-500 to-purple-500',
        bgGradient: 'from-pink-50 to-purple-50',
        primary: 'pink',
        reportTitle: 'Reportes de Belleza',
        icons: {
          revenue: <DollarSign className="w-6 h-6 text-pink-600" />,
          appointments: <Scissors className="w-6 h-6 text-pink-600" />,
          ticket: <Gem className="w-6 h-6 text-pink-600" />,
          retention: <Target className="w-6 h-6 text-pink-600" />
        }
      };
    } else {
      return {
        gradient: 'from-blue-500 to-green-500',
        bgGradient: 'from-blue-50 to-green-50',
        primary: 'blue',
        reportTitle: 'Reportes Medicos',
        icons: {
          revenue: <DollarSign className="w-6 h-6 text-blue-600" />,
          appointments: <Stethoscope className="w-6 h-6 text-blue-600" />,
          ticket: <BarChart3 className="w-6 h-6 text-blue-600" />,
          retention: <TrendingUp className="w-6 h-6 text-blue-600" />
        }
      };
    }
  };

  const [theme, setTheme] = useState<ThemeConfig>(getThemeConfig('belleza'));

  useEffect(() => {
    // Verificar usuario
    const userData = localStorage.getItem('user');
    if (userData) {
      const parsedUser = JSON.parse(userData);
      setUser(parsedUser);
      setTheme(getThemeConfig(parsedUser.professional_type || 'belleza'));
    }
  }, []);

  useEffect(() => {
    if (user?.id) {
      loadReportData();
    }
  }, [selectedPeriod, user?.id]);

  const loadReportData = async () => {
    if (!user?.id) return;
    
    setLoading(true);
    setError(null);

    try {
      console.log(`📊 Cargando reportes para profesional ${user.id}, período: ${selectedPeriod}`);
      
      const API_URL = process.env.REACT_APP_API_URL || 'https://consultar-api.onrender.com';
      // El endpoint pide token: sin esto cualquiera podia leer la facturacion
      // de cualquier profesional escribiendo su id en la URL.
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/api/reports/professional/${user.id}?period=${selectedPeriod}`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      
      if (!response.ok) {
        // Si la ruta no existe, usar datos mock por ahora
        if (response.status === 404) {
          console.log('📊 Ruta de reportes no existe, usando datos mock');
          await loadMockData();
          return;
        }
        throw new Error('Error cargando reportes');
      }

      const data = await response.json();
      
      if (data.success) {
        setReportData(data.reports);
      } else {
        throw new Error(data.message || 'Error en la respuesta del servidor');
      }

    } catch (err: any) {
      console.error('❌ Error cargando reportes:', err);
      
      // Si hay error, cargar datos mock como fallback
      console.log('📊 Usando datos mock como fallback');
      await loadMockData();
    } finally {
      setLoading(false);
    }
  };

  const loadMockData = async () => {
    // Simular delay de API
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    const mockData: ReportData = {
      totalRevenue: user?.professional_type === 'belleza' ? 85000 : 120000,
      totalAppointments: user?.professional_type === 'belleza' ? 42 : 38,
      avgTicket: user?.professional_type === 'belleza' ? 2024 : 3158,
      clientRetention: user?.professional_type === 'belleza' ? 78 : 85,
      popularServices: user?.professional_type === 'belleza' 
        ? [
            { name: 'Extensiones de Pestañas', count: 18, revenue: 36000 },
            { name: 'Manicura', count: 15, revenue: 22500 },
            { name: 'Cejas', count: 9, revenue: 13500 }
          ]
        : [
            { name: 'Consulta General', count: 15, revenue: 45000 },
            { name: 'Control Preventivo', count: 12, revenue: 36000 },
            { name: 'Seguimiento', count: 11, revenue: 33000 }
          ],
      monthlyTrend: [
        { month: 'Enero', appointments: 25, revenue: 50000 },
        { month: 'Febrero', appointments: 28, revenue: 56000 },
        { month: 'Marzo', appointments: 32, revenue: 64000 },
        { month: 'Abril', appointments: 30, revenue: 60000 }
      ],
      topClients: [
        { name: 'María García', totalSpent: 8500, appointments: 12 },
        { name: 'Ana López', totalSpent: 6200, appointments: 8 },
        { name: 'Carmen Silva', totalSpent: 5800, appointments: 7 }
      ]
    };
    
    setReportData(mockData);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const getPeriodText = () => {
    switch (selectedPeriod) {
      case 'week': return 'Esta semana';
      case 'month': return 'Este mes';
      case 'quarter': return 'Este trimestre';
      case 'year': return 'Este año';
      default: return 'Este mes';
    }
  };

  if (!user) {
    return (
      <div className={`min-h-screen bg-gradient-to-br ${theme.bgGradient} flex items-center justify-center`}>
        <div className="text-center">
          <p className="text-red-600">Error: Usuario no encontrado</p>
          <button 
            onClick={() => navigate('/login')}
            className={`mt-4 bg-gradient-to-r ${theme.gradient} text-white px-4 py-2 rounded-lg hover:shadow-lg transition-shadow`}
          >
            Ir a Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-gradient-to-br ${theme.bgGradient}`}>
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2"><BarChart3 className="w-6 h-6" /> {theme.reportTitle}</h1>
              <p className="text-gray-600">
                Análisis de tu {user.professional_type === 'belleza' ? 'negocio' : 'consulta'}
                {reportData && <span className="ml-2">• {getPeriodText()}</span>}
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <select
                value={selectedPeriod}
                onChange={(e) => setSelectedPeriod(e.target.value)}
                className={`px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-${theme.primary}-500 focus:border-transparent`}
              >
                <option value="week">Esta semana</option>
                <option value="month">Este mes</option>
                <option value="quarter">Este trimestre</option>
                <option value="year">Este año</option>
              </select>
              <button
                onClick={() => loadReportData()}
                disabled={loading}
                className={`bg-gradient-to-r ${theme.gradient} text-white px-4 py-2 rounded-lg hover:shadow-lg transition-shadow disabled:opacity-50`}
              >
                {loading ? 'Cargando...' : 'Actualizar'}
              </button>
              <button 
                onClick={() => navigate(-1)} 
                className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors"
              >
                ← Volver
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Página pública: visitas, turnos y conversión. Va arriba de todo y
            fuera del `loading` de abajo porque carga sus propios datos: no
            tiene por qué esperar al reporte de facturación. */}
        <div className="mb-8">
          <VisitasCard />
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-6 mb-8">
            <p className="text-red-600 mb-4">Error: {error}</p>
            <button 
              onClick={() => loadReportData()}
              className="bg-red-100 text-red-600 px-4 py-2 rounded-lg hover:bg-red-200"
            >
              Reintentar
            </button>
          </div>
        )}

        {loading ? (
          <div className="space-y-8">
            {/* KPIs skeleton */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="bg-white rounded-xl shadow-sm border p-6 animate-pulse">
                  <div className="h-4 bg-gray-200 rounded mb-2"></div>
                  <div className="h-8 bg-gray-200 rounded"></div>
                </div>
              ))}
            </div>
            
            {/* Charts skeleton */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="bg-white rounded-xl shadow-sm border p-6 animate-pulse">
                <div className="h-64 bg-gray-200 rounded"></div>
              </div>
              <div className="bg-white rounded-xl shadow-sm border p-6 animate-pulse">
                <div className="h-64 bg-gray-200 rounded"></div>
              </div>
            </div>
          </div>
        ) : reportData ? (
          <>
            {/* KPIs principales */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {/* Ingresos Totales */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Ingresos Totales</p>
                    <p className="text-2xl font-bold text-gray-900">{formatCurrency(reportData.totalRevenue)}</p>
                  </div>
                  <div className={`w-12 h-12 bg-${theme.primary}-100 rounded-lg flex items-center justify-center`}>
                    {theme.icons.revenue}
                  </div>
                </div>
              </div>

              {/* Total Citas */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">
                      Total {user.professional_type === 'belleza' ? 'Citas' : 'Consultas'}
                    </p>
                    <p className="text-2xl font-bold text-gray-900">{reportData.totalAppointments}</p>
                  </div>
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                    {theme.icons.appointments}
                  </div>
                </div>
              </div>

              {/* Ticket Promedio */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Ticket Promedio</p>
                    <p className="text-2xl font-bold text-gray-900">{formatCurrency(reportData.avgTicket)}</p>
                  </div>
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                    {theme.icons.ticket}
                  </div>
                </div>
              </div>

              {/* Retención */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Retención</p>
                    <p className="text-2xl font-bold text-gray-900">{reportData.clientRetention}%</p>
                  </div>
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    {theme.icons.retention}
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
              {/* Servicios Más Populares */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">
                  🔥 {user.professional_type === 'belleza' ? 'Servicios' : 'Consultas'} Más Populares
                </h3>
                <div className="space-y-4">
                  {reportData.popularServices.map((service, index) => (
                    <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900">{service.name}</h4>
                        <p className="text-sm text-gray-600">{service.count} veces</p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-gray-900">{formatCurrency(service.revenue)}</p>
                        <p className="text-sm text-gray-600">
                          {formatCurrency(Math.round(service.revenue / service.count))} promedio
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Tendencia Mensual */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2"><TrendingUp className="w-5 h-5" /> Tendencia Mensual</h3>
                <div className="space-y-4">
                  {reportData.monthlyTrend.map((month, index) => (
                    <div key={index} className="flex items-center justify-between p-3 border-b border-gray-100 last:border-b-0">
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900">{month.month}</h4>
                        <p className="text-sm text-gray-600">{month.appointments} citas</p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-gray-900">{formatCurrency(month.revenue)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Top Clientes */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-6">
                ⭐ Mejores {user.professional_type === 'belleza' ? 'Clientas' : 'Pacientes'}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {reportData.topClients.map((client, index) => (
                  <div key={index} className="text-center p-4 bg-gray-50 rounded-lg">
                    <div className={`w-12 h-12 bg-gradient-to-r ${theme.gradient} rounded-full flex items-center justify-center mx-auto mb-3`}>
                      <span className="text-white font-bold">{index + 1}</span>
                    </div>
                    <h4 className="font-semibold text-gray-900 mb-1">{client.name}</h4>
                    <p className="text-lg font-bold text-green-600">{formatCurrency(client.totalSpent)}</p>
                    <p className="text-sm text-gray-600">{client.appointments} visitas</p>
                  </div>
                ))}
              </div>
            </div>
          </>
        ) : (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <BarChart2 className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No hay datos disponibles</h3>
            <p className="text-gray-600 mb-6">
              No se pudieron cargar los reportes para este período
            </p>
            <button
              onClick={() => loadReportData()}
              className={`bg-gradient-to-r ${theme.gradient} text-white px-6 py-2 rounded-lg hover:shadow-lg transition-shadow`}
            >
              Reintentar
            </button>
          </div>
        )}

        
      </div>
    </div>
  );
};

export default Reports;